// NOTE: Storage implementation migrated to Supabase under-the-hood while preserving the existing API.
import { uploadFile as supaUpload, getPublicUrl as supaPublicUrl, removeFile as supaRemove, listUserFiles as supaListUserFiles } from '@/integrations/supabase/storage';

// File type definitions
export type FileType = 'profile' | 'document' | 'application' | 'appointment' | 'general';

export interface UploadResult {
  url: string;
  path: string;
  metadata: {
    name: string;
    size: number;
    type: string;
    uploadedAt: Date;
  };
}

export interface FileMetadata {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
  path: string;
  uploadedAt: Date;
  userId: string;
  category: FileType;
}

export class FirebaseStorageService {
  // Generate organized file paths
  static generateFilePath(userId: string, fileType: FileType, fileName: string, subfolder?: string): string {
    const timestamp = Date.now();
    const fileExtension = fileName.split('.').pop();
    const sanitizedName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
    
    if (subfolder) {
      return `${fileType}/${userId}/${subfolder}/${timestamp}_${sanitizedName}`;
    }
    return `${fileType}/${userId}/${timestamp}_${sanitizedName}`;
  }

  // Upload file with metadata
  static async uploadFile(
    file: File,
    userId: string,
    fileType: FileType,
    subfolder?: string,
    customMetadata?: { [key: string]: string }
  ): Promise<UploadResult> {
    try {
      const filePath = this.generateFilePath(userId, fileType, file.name, subfolder);
      const bucket = 'user-files';
      await supaUpload(bucket, filePath, file);
      const downloadURL = supaPublicUrl(bucket, filePath);

      return {
        url: downloadURL,
        path: filePath,
        metadata: {
          name: file.name,
          size: file.size,
          type: file.type,
          uploadedAt: new Date()
        }
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  // Upload multiple files
  static async uploadMultipleFiles(
    files: File[],
    userId: string,
    fileType: FileType,
    subfolder?: string
  ): Promise<UploadResult[]> {
    const uploadPromises = files.map(file => 
      this.uploadFile(file, userId, fileType, subfolder)
    );
    
    return Promise.all(uploadPromises);
  }

  // Delete file
  static async deleteFile(path: string): Promise<void> {
    try {
      const bucket = 'user-files';
      await supaRemove(bucket, path);
    } catch (error) {
      console.error('Error deleting file:', error);
      throw error;
    }
  }

  // Get file metadata
  static async getFileMetadata(path: string): Promise<any> {
    try {
      // Supabase Storage doesn't expose metadata like Firebase; return minimal data
      return { fullPath: path };
    } catch (error) {
      console.error('Error getting file metadata:', error);
      throw error;
    }
  }

  // List user files by type
  static async listUserFiles(userId: string, fileType: FileType): Promise<FileMetadata[]> {
    try {
      return await supaListUserFiles(userId, fileType);
    } catch (error) {
      console.error('Error listing user files:', error);
      throw error;
    }
  }

  // Validate file type and size
  static validateFile(file: File, allowedTypes: string[], maxSizeMB: number = 10): { valid: boolean; error?: string } {
    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `File type ${file.type} is not allowed. Allowed types: ${allowedTypes.join(', ')}`
      };
    }

    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return {
        valid: false,
        error: `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum allowed size of ${maxSizeMB}MB`
      };
    }

    return { valid: true };
  }

  // Get file size in human readable format
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Generate thumbnail for images (placeholder - you can implement actual thumbnail generation)
  static async generateThumbnail(file: File): Promise<File> {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        const maxSize = 200;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx?.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const thumbnailFile = new File([blob], `thumb_${file.name}`, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(thumbnailFile);
          } else {
            resolve(file);
          }
        }, 'image/jpeg', 0.8);
      };
      
      img.src = URL.createObjectURL(file);
    });
  }
}
